import React, { useCallback, useMemo, useState } from 'react';

import noop from 'lodash/noop';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { AgenticPlanWrapperInChat, type ChildrenProps } from './AgenticPlanWrapperInChat';
import { CollapsibleCard } from './CollapsibleCard';
import {
	MultipleChoiceAnswers,
	parseAnswersByQuestionId,
	type AskQuestionCompletedViewProps,
} from './MultipleChoiceAnswers';
import { MultipleChoiceQuestionsTool } from './MultipleChoiceQuestionsTool.tsx';
import type { AdditionalAnalyticAttributes, OnConfirmPayload } from './types';

type Props = ActionRendererFor<typeof ActionKeys.MultipleChoiceQuestionsTool>;

const hasActionsPayload = (
	payload?: OnConfirmPayload,
): payload is Extract<OnConfirmPayload, { actions: unknown[] }> =>
	Boolean(
		payload && 'actions' in payload && Array.isArray(payload.actions) && payload.actions.length > 0,
	);

const MultipleChoiceQuestionsToolInChatComponent = ({
	message,
	onConfirm,
	handleOnDismiss,
	recipientAgentName,
	localActionState,
	ContentRenderer,
	productKey,
	cloudId,
	messageId,
	AskQuestionCompletedView,
}: ChildrenProps &
	Pick<Props, 'ContentRenderer' | 'productKey' | 'cloudId'> & {
		AskQuestionCompletedView: React.ComponentType<AskQuestionCompletedViewProps>;
		messageId: string;
		recipientAgentName?: string;
	}) => {
	const [confirmedAnswers, setConfirmedAnswers] = useState<Record<string, string | null>>({});

	const action = useMemo(() => message.actions[0], [message.actions]);

	const handleOnConfirm = useCallback(
		(
			_event: React.MouseEvent | undefined,
			_analyticsEvent: UIAnalyticsEvent | undefined,
			payload?: OnConfirmPayload,
			analyticsAttributes?: AdditionalAnalyticAttributes,
		) => {
			if (hasActionsPayload(payload)) {
				setConfirmedAnswers(parseAnswersByQuestionId(payload.actions[0]?.data));
				onConfirm(
					{ actions: payload.actions },
					{
						actionKey: action?.key,
						...analyticsAttributes,
					},
				);
			}

			return Promise.resolve();
		},
		[onConfirm, action.key],
	);

	if (localActionState === 'finished') {
		return (
			<MultipleChoiceAnswers
				action={action}
				confirmedAnswers={confirmedAnswers}
				ContentRenderer={ContentRenderer}
				productKey={productKey}
				cloudId={cloudId}
				messageId={messageId}
				AskQuestionCompletedView={AskQuestionCompletedView}
			/>
		);
	}

	return (
		<CollapsibleCard>
			<MultipleChoiceQuestionsTool
				agentSessionInfo={{ agentName: recipientAgentName ?? '' }}
				message={message}
				onConfirm={handleOnConfirm}
				onDismissSession={handleOnDismiss}
				onOpenRovoChat={noop}
			/>
		</CollapsibleCard>
	);
};

type MultipleChoiceQuestionsToolInChatProps = Props & {
	AskQuestionCompletedView: React.ComponentType<AskQuestionCompletedViewProps>;
};

export const MultipleChoiceQuestionsToolInChat = (
	props: MultipleChoiceQuestionsToolInChatProps,
): React.JSX.Element => (
	<AgenticPlanWrapperInChat
		{...props}
		key={props.actions[0]?.invocationId}
		interactionSource="multipleChoiceQuestionsToolRenderer"
	>
		{(wrapperProps) => (
			<MultipleChoiceQuestionsToolInChatComponent
				{...wrapperProps}
				recipientAgentName={props.recipientAgentName}
				ContentRenderer={props.ContentRenderer}
				productKey={props.productKey}
				cloudId={props.cloudId}
				messageId={props.messageId}
				AskQuestionCompletedView={props.AskQuestionCompletedView}
			/>
		)}
	</AgenticPlanWrapperInChat>
);
